//#region IMPORT
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useState, useEffect } from 'react';
import { useSearch3 } from '../../../BasicInfo/Member/MEM_BASIC/MEM_BASIC_QUERY';
import DatePickerBasic from '../../../../Components/MUIs/DatePickerBasic';
import ButtonBasic from '../../../../Components/MUIs/ButtonBasic';
import ComboBasic from '../../../../Components/MUIs/ComboBasic';
//#endregion

/**
 * 대표자 선택 모달 (폼 형식)
 * 하나의 데이터를 저장하기 위한 폼
 */
function SelectRepresentativeModal({
  open,
  onClose,
  onSelect,
  userInfo,
  setInputs,
  inputs,
  companyInfo = null,
}) {
  const [errors, setErrors] = useState({});
  const [authCodeList, setAuthCodeList] = useState([]);
  const existingMemberId = companyInfo?.id || companyInfo?.Id || '';

  // 권한 목록 조회
  const { refetch: searchAuth } = useSearch3({
    onSuccess: (formattedList) => {
      setAuthCodeList(formattedList);
    },
  });

  useEffect(() => {
    if (open) {
      setErrors({});
      searchAuth();
      // companyInfo가 있으면 초기값 설정
      if (companyInfo) {
        const birthSource = companyInfo.birth || '';
        const normalizedBirth = birthSource
          ? dayjs(birthSource).isValid()
            ? dayjs(birthSource).format('YYYY-MM-DD')
            : birthSource
          : '';
        const normalizedAddress =
          companyInfo.address ||
          [companyInfo.address1, companyInfo.address2]
            .filter(Boolean)
            .join(' ') ||
          '';
        setInputs((prev) => ({
          ...prev,
          name: companyInfo.name || prev.name,
          id: companyInfo.id || companyInfo.Id || prev.id,
          position: companyInfo.position || prev.position,
          rank: companyInfo.rank || prev.rank,
          tel: companyInfo.tel || prev.tel,
          depNm1: companyInfo.depNm1 || prev.depNm1,
          depNm2: companyInfo.depNm2 || prev.depNm2,
          depNm3: companyInfo.depNm3 || prev.depNm3,
          depNm4: companyInfo.depNm4 || prev.depNm4,
          nation: companyInfo.nation || prev.nation,
          bloodType: companyInfo.bloodType || prev.bloodType,
          birth: normalizedBirth || prev.birth,
          address: normalizedAddress || prev.address,
        }));
      }
    }
  }, [open, companyInfo]);

  const handleChange = (field, value) => {
    setInputs((prev) => ({
      ...prev,
      [field]: value,
    }));
    // 에러 초기화
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!inputs.depNm1 || !inputs.depNm1.trim()) {
      newErrors.depNm1 = '본부는 필수입니다.';
    }
    if (!inputs.name || !inputs.name.trim()) {
      newErrors.name = '성명은 필수입니다.';
    }
    if (!existingMemberId) {
      if (!inputs.id || !String(inputs.id).trim()) {
        newErrors.id = '회원 ID는 필수입니다.';
      }
    }
    if (!inputs.birth || !inputs.birth.trim()) {
      newErrors.birth = '생년월일은 필수입니다.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }

    const memberData = {
      ...inputs,
      newYn: 'Y',
    };
    const trimmedId = String(inputs.id || '').trim();
    onSelect({
      coId: trimmedId,
      name: inputs.name,
      memberData: { ...memberData, id: trimmedId },
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: '1px solid rgba(231,237,243,1)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid rgba(231,237,243,1)',
          px: 3,
          py: 2,
        }}
      >
        <Typography sx={{ fontSize: '1.25rem', fontWeight: 700 }}>
          대표자 정보 입력
        </Typography>
        <IconButton
          onClick={onClose}
          sx={{
            color: 'text.secondary',
            '&:hover': { color: 'text.primary' },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 3 }}>
        <Box sx={{ mb: 3, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '1rem', fontWeight: 700 }}>
            회원정보
          </Typography>
        </Box>

        <Box
          sx={{
            border: '1px solid rgba(231,237,243,1)',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {/* 첫 번째 행: 본부, 부서 */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              borderBottom: '1px solid rgba(231,237,243,1)',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                borderRight: { md: '1px solid rgba(231,237,243,1)' },
                borderBottom: {
                  xs: '1px solid rgba(231,237,243,1)',
                  md: 'none',
                },
              }}
            >
              <Box
                sx={{
                  py: 2,
                  px: 2,
                  bgcolor: 'rgba(248,250,252,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textAlign: 'center',
                  }}
                >
                  본부
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, position: 'relative' }}>
                <Typography
                  sx={{
                    position: 'absolute',
                    top: -8,
                    left: 8,
                    color: '#10b981',
                    fontWeight: 700,
                    fontSize: '1.125rem',
                  }}
                >
                  *
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={inputs.depNm1 || ''}
                  onChange={(e) => handleChange('depNm1', e.target.value)}
                  error={!!errors.depNm1}
                  helperText={errors.depNm1}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderColor: errors.depNm1 ? '#ef4444' : '#10b981',
                      '&:hover': {
                        borderColor: errors.depNm1 ? '#ef4444' : '#10b981',
                      },
                      '&.Mui-focused': {
                        borderColor: errors.depNm1 ? '#ef4444' : '#137fec',
                      },
                    },
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
              }}
            >
              <Box
                sx={{
                  py: 2,
                  px: 2,
                  bgcolor: 'rgba(248,250,252,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textAlign: 'center',
                  }}
                >
                  부서
                </Typography>
              </Box>
              <Box sx={{ p: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={inputs.depNm2 || ''}
                  onChange={(e) => handleChange('depNm2', e.target.value)}
                />
              </Box>
            </Box>
          </Box>

          {/* 두 번째 행: 팀, 파트 */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              borderBottom: '1px solid rgba(231,237,243,1)',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                borderRight: { md: '1px solid rgba(231,237,243,1)' },
                borderBottom: {
                  xs: '1px solid rgba(231,237,243,1)',
                  md: 'none',
                },
              }}
            >
              <Box
                sx={{
                  py: 2,
                  px: 2,
                  bgcolor: 'rgba(248,250,252,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textAlign: 'center',
                  }}
                >
                  팀
                </Typography>
              </Box>
              <Box sx={{ p: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={inputs.depNm3 || ''}
                  onChange={(e) => handleChange('depNm3', e.target.value)}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
              }}
            >
              <Box
                sx={{
                  py: 2,
                  px: 2,
                  bgcolor: 'rgba(248,250,252,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textAlign: 'center',
                  }}
                >
                  파트
                </Typography>
              </Box>
              <Box sx={{ p: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={inputs.depNm4 || ''}
                  onChange={(e) => handleChange('depNm4', e.target.value)}
                />
              </Box>
            </Box>
          </Box>

          {/* 세 번째 행: 권한, 직책 */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              borderBottom: '1px solid rgba(231,237,243,1)',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                borderRight: { md: '1px solid rgba(231,237,243,1)' },
                borderBottom: {
                  xs: '1px solid rgba(231,237,243,1)',
                  md: 'none',
                },
              }}
            >
              <Box
                sx={{
                  py: 2,
                  px: 2,
                  bgcolor: 'rgba(248,250,252,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textAlign: 'center',
                  }}
                >
                  안전보건조직
                </Typography>
              </Box>
              <Box sx={{ p: 1.5 }}>
                <ComboBasic
                  items={[
                    { value: '사업주', name: '사업주' },
                    { value: '안전관리자', name: '안전관리자' },
                    { value: '보건관리자', name: '보건관리자' },
                    {
                      value: '안전보건관리담당자',
                      name: '안전보건관리담당자',
                    },
                    { value: '산업보건의', name: '산업보건의' },
                    { value: '관리감독자', name: '관리감독자' },
                    {
                      value: '안전보건관리책임자',
                      name: '안전보건관리책임자',
                    },
                    {
                      value: '안전보건총괄책임자',
                      name: '안전보건총괄책임자',
                    },
                    {
                      value: '명예산업안전감독관',
                      name: '명예산업안전감독관',
                    },
                    {
                      value: '중대재해 전담조직',
                      name: '중대재해 전담조직',
                    },
                  ]}
                  value={inputs.position || '사업주'}
                  onChange={(value) => handleChange('position', value)}
                  fullWidth
                  size="small"
                  name=""
                  wrapSx={{ m: 0 }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderColor: '#10b981',
                      '&:hover': {
                        borderColor: '#10b981',
                      },
                      '&.Mui-focused': {
                        borderColor: '#137fec',
                      },
                    },
                  }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
              }}
            >
              <Box
                sx={{
                  py: 2,
                  px: 2,
                  bgcolor: 'rgba(248,250,252,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textAlign: 'center',
                  }}
                >
                  국적
                </Typography>
              </Box>
              <Box sx={{ p: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={inputs.nation || '대한민국'}
                  onChange={(e) => handleChange('nation', e.target.value)}
                />
              </Box>
            </Box>
          </Box>

          {/* 네 번째 행: 직위, 성명 */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              borderBottom: '1px solid rgba(231,237,243,1)',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                borderRight: { md: '1px solid rgba(231,237,243,1)' },
                borderBottom: {
                  xs: '1px solid rgba(231,237,243,1)',
                  md: 'none',
                },
              }}
            >
              <Box
                sx={{
                  py: 2,
                  px: 2,
                  bgcolor: 'rgba(248,250,252,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textAlign: 'center',
                  }}
                >
                  직위
                </Typography>
              </Box>
              <Box sx={{ p: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={inputs.rank || ''}
                  onChange={(e) => handleChange('rank', e.target.value)}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
              }}
            >
              <Box
                sx={{
                  py: 2,
                  px: 2,
                  bgcolor: 'rgba(248,250,252,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textAlign: 'center',
                  }}
                >
                  성명
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, position: 'relative' }}>
                <Typography
                  sx={{
                    position: 'absolute',
                    top: -8,
                    left: 8,
                    color: '#10b981',
                    fontWeight: 700,
                    fontSize: '1.125rem',
                  }}
                >
                  *
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={inputs.name || ''}
                  onChange={(e) => handleChange('name', e.target.value)}
                  error={!!errors.name}
                  helperText={errors.name}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderColor: errors.name ? '#ef4444' : '#10b981',
                      '&:hover': {
                        borderColor: errors.name ? '#ef4444' : '#10b981',
                      },
                      '&.Mui-focused': {
                        borderColor: errors.name ? '#ef4444' : '#137fec',
                      },
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* 다섯 번째 행: ID (신규 회원은 ID 필수 — 서버 SaveMemBasicU1) */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '120px 1fr',
              borderBottom: '1px solid rgba(231,237,243,1)',
            }}
          >
            <Box
              sx={{
                py: 2,
                px: 2,
                bgcolor: 'rgba(248,250,252,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  textAlign: 'center',
                }}
              >
                ID
              </Typography>
            </Box>
            <Box sx={{ p: 1.5, position: 'relative' }}>
              {!existingMemberId && (
                <Typography
                  sx={{
                    position: 'absolute',
                    top: -8,
                    left: 8,
                    color: '#10b981',
                    fontWeight: 700,
                    fontSize: '1.125rem',
                  }}
                >
                  *
                </Typography>
              )}
              <TextField
                fullWidth
                size="small"
                value={inputs.id || ''}
                onChange={(e) => handleChange('id', e.target.value)}
                disabled={Boolean(existingMemberId)}
                placeholder="로그인 회원 ID"
                error={!!errors.id}
                helperText={
                  errors.id ||
                  (existingMemberId
                    ? '등록된 회원 ID입니다.'
                    : '신규 등록 시 필수입니다.')
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: existingMemberId ? 'rgba(0,0,0,0.04)' : undefined,
                    borderColor: errors.id ? '#ef4444' : '#10b981',
                    '&:hover': {
                      borderColor: errors.id ? '#ef4444' : '#10b981',
                    },
                    '&.Mui-focused': {
                      borderColor: errors.id ? '#ef4444' : '#137fec',
                    },
                  },
                }}
              />
            </Box>
          </Box>

          {/* 여섯 번째 행: 전화번호, 생년월일 */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              borderBottom: '1px solid rgba(231,237,243,1)',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                borderRight: { md: '1px solid rgba(231,237,243,1)' },
                borderBottom: {
                  xs: '1px solid rgba(231,237,243,1)',
                  md: 'none',
                },
              }}
            >
              <Box
                sx={{
                  py: 2,
                  px: 2,
                  bgcolor: 'rgba(248,250,252,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textAlign: 'center',
                  }}
                >
                  전화번호
                </Typography>
              </Box>
              <Box sx={{ p: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={inputs.tel || ''}
                  onChange={(e) => {
                    let value = e.target.value.replace(/[^\d]/g, ''); // 숫자만 허용
                    // 000-0000-0000 형식으로 포맷팅
                    if (value.length <= 3) {
                      value = value;
                    } else if (value.length <= 7) {
                      value = value.slice(0, 3) + '-' + value.slice(3);
                    } else {
                      value =
                        value.slice(0, 3) +
                        '-' +
                        value.slice(3, 7) +
                        '-' +
                        value.slice(7, 11);
                    }
                    handleChange('tel', value);
                  }}
                  placeholder="000-0000-0000"
                  inputProps={{
                    inputMode: 'tel',
                    maxLength: 13, // 000-0000-0000 = 13자
                    pattern: '[0-9]{3}-[0-9]{4}-[0-9]{4}',
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
              }}
            >
              <Box
                sx={{
                  py: 2,
                  px: 2,
                  bgcolor: 'rgba(248,250,252,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textAlign: 'center',
                  }}
                >
                  생년월일
                </Typography>
              </Box>
              <Box sx={{ p: 1.5, position: 'relative' }}>
                <Typography
                  sx={{
                    position: 'absolute',
                    top: -8,
                    left: 8,
                    color: '#10b981',
                    fontWeight: 700,
                    fontSize: '1.125rem',
                  }}
                >
                  *
                </Typography>
                <DatePickerBasic
                  value={inputs.birth ? dayjs(inputs.birth) : null}
                  onChange={(date) => {
                    if (date) {
                      handleChange('birth', dayjs(date).format('YYYY-MM-DD'));
                    } else {
                      handleChange('birth', '');
                    }
                  }}
                  format="YYYY-MM-DD"
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      error: !!errors.birth,
                      helperText: errors.birth,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderColor: errors.birth ? '#ef4444' : '#10b981',
                          '&:hover': {
                            borderColor: errors.birth ? '#ef4444' : '#10b981',
                          },
                          '&.Mui-focused': {
                            borderColor: errors.birth ? '#ef4444' : '#137fec',
                          },
                        },
                      },
                    },
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* 여덟 번째 행: 혈액형, 주소 */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              borderBottom: '1px solid rgba(231,237,243,1)',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
                borderRight: { md: '1px solid rgba(231,237,243,1)' },
                borderBottom: {
                  xs: '1px solid rgba(231,237,243,1)',
                  md: 'none',
                },
              }}
            >
              <Box
                sx={{
                  py: 2,
                  px: 2,
                  bgcolor: 'rgba(248,250,252,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textAlign: 'center',
                  }}
                >
                  혈액형
                </Typography>
              </Box>
              <Box sx={{ p: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={inputs.bloodType || ''}
                  onChange={(e) => handleChange('bloodType', e.target.value)}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '120px 1fr',
              }}
            >
              <Box
                sx={{
                  py: 2,
                  px: 2,
                  bgcolor: 'rgba(248,250,252,0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 700,
                    textAlign: 'center',
                  }}
                >
                  주소
                </Typography>
              </Box>
              <Box sx={{ p: 1.5 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={inputs.address || ''}
                  onChange={(e) => handleChange('address', e.target.value)}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          bgcolor: 'rgba(248,250,252,0.5)',
          borderTop: '1px solid rgba(231,237,243,1)',
          gap: 1.5,
        }}
      >
        <ButtonBasic
          name="취소"
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: 'rgba(203,213,225,1)',
            color: 'text.primary',
            fontWeight: 600,
            '&:hover': {
              borderColor: 'rgba(148,163,184,1)',
              bgcolor: 'rgba(248,250,252,1)',
            },
          }}
        />
        <ButtonBasic
          name="저장하기"
          onClick={handleSave}
          variant="contained"
          sx={{
            bgcolor: '#137fec',
            fontWeight: 600,
            boxShadow: '0 2px 4px rgba(19,127,236,0.2)',
            '&:hover': {
              bgcolor: '#0f6dd1',
              boxShadow: '0 4px 8px rgba(19,127,236,0.3)',
            },
          }}
        />
      </DialogActions>
    </Dialog>
  );
}

export default SelectRepresentativeModal;
